// apps/admin/src/features/products/TaxonomyTable.tsx
//
// dbg-20260803-025413 — shared list table for the Category & Collection page
// (Figma "Fulfillment V3" frames `category-management` + `collection-management`).
//
// The two frames are the same table; Collection adds one LABEL column between
// NAME and IMAGE. That is the only structural difference, so both tabs render
// this component and Collection passes `showLabel`.
//
// Measurements come from the Figma node tree (11:4), not from eyeballing:
//   card            r=8, 1px border
//   header row      h=36, fill #F1F5F9, 12px/600 uppercase, #475569
//   body row        h=56, zebra #FFFFFF / #F8FAFC, 1px bottom border
//   columns         grip 40 | name 280 | (label 120) | image 80 | created 180 |
//                   lastUpdate 180 | updatedBy 200 | status 100 | display 100 |
//                   actions 100
//
// CREATED DATE is not in the Figma frame — added at the user's request
// (dbg-20260803-071721) together with the switch to newest-created-first
// ordering, so the column the list is sorted by is actually visible. NAME and
// UPDATED BY give back the width it takes.
//   name parent     14px/600 #0F172A     name child   14px/400 #0F172A
//   date/updatedBy  13px/400 #475569
//   action buttons  26x26, r=6, 1px border
//
// Colours are pw-* tokens rather than the raw Figma hexes: the project ships a
// dark theme and the dark-mode audit rejects hardcoded light values. The token
// palette is a near-match in light mode (page #f9fafb vs #FAFAFA, border
// #e5e7eb vs #E2E8F0), and the brand orange comes from --pw-primary-bg, which
// is the project's own brand value rather than the mock's #F06022.
//
// Row ORDER is the server's (sortOrder ASC, newest-created first as the
// tiebreak, BR-3 children pinned under their parent) and is rendered verbatim.
// Grouping here only powers the expand/collapse caret and the child indent.
//
// Drag-drop changes ROW POSITION, not parentage (user, dbg-20260811-022808 —
// this reverses dbg-20260808-031625, which read the same requirement as a
// parentage change). A drag moves a row up or down among its OWN siblings and
// nothing else: dropping across levels is refused, so a drag can never turn a
// root into a child or move a child to another parent. Re-parenting has one
// surface only — Edit → Parent Category.
//
// It uses HTML5 dnd (no @dnd-kit in apps/admin). Same-level is enforced here
// AND by the server, which answers a cross-level payload with 409
// CATEGORY_REORDER_CROSS_LEVEL.
//
// The reorder POST carries a RELATIVE order; the server renumbers the whole
// sibling level from it, so a drag on page 2 does not collide with page 1.

import { Fragment, useMemo, useRef, useState, type DragEvent, type JSX } from 'react';

import { Switch, cn, formatDateTimeFixed } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Row contract — structurally satisfied by both CategoryRow and CollectionRow.
// ---------------------------------------------------------------------------

export interface TaxonomyTableRow {
  id: string;
  name: { en: string; vi?: string | undefined; fr?: string | undefined };
  parentId: string | null;
  status: 'Active' | 'Inactive';
  display?: boolean | undefined;
  image?: string | null | undefined;
  createdAt?: number | undefined;
  updatedAt?: number | undefined;
  updatedByEmail?: string | null | undefined;
  /** Collection only — merchandising badge text ("Hot", "New", "Sale"). */
  label?: string | null | undefined;
  /** Collection only — hex colour for the label text. */
  labelColor?: string | null | undefined;
  /** Server-side count of direct children. */
  childCount?: number | undefined;
}

export interface TaxonomyTableProps<TRow extends TaxonomyTableRow> {
  rows: TRow[];
  /** Renders the Collection-only LABEL column. */
  showLabel?: boolean;
  onToggleStatus: (row: TRow, next: boolean) => void;
  onToggleDisplay: (row: TRow, next: boolean) => void;
  onEdit: (row: TRow) => void;
  onViewProducts: (row: TRow) => void;
  /** Receives the level's rows in their new order; caller derives sortOrder. */
  onReorderSiblings: (siblings: TRow[]) => void;
  busy?: boolean;
  /** Distinguishes the two tabs' test ids. */
  testIdPrefix?: string;
}

interface Group<TRow> {
  parent: TRow;
  children: TRow[];
}

/** `parentId` is the level the drag is confined to — the drop check reads it. */
interface DragState {
  id: string;
  parentId: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Category.md BR-8 — Last Update (and Created) render as `dd/MM/yyyy HH:mm:ss`.
 *
 * Fixed by the business doc, so this is deliberately NOT locale-formatted:
 * a hardcoded locale tag is banned by the project i18n rule, and the browser
 * locale would reorder the parts.
 *
 * dbg-20260810 — now resolved against the topbar timezone. The hand-built
 * version used `d.getDate()` / `d.getHours()`, which are browser-zone-only.
 */
function formatLastUpdate(epochMs: number | undefined): string {
  return formatDateTimeFixed(epochMs);
}

/**
 * Group server-ordered flat rows into parent → children, preserving the order
 * the server sent. A child whose parent is absent from this page (the group
 * straddled a page boundary, or search matched only the child) becomes its own
 * top-level group so it is never dropped.
 */
function groupRows<TRow extends TaxonomyTableRow>(rows: readonly TRow[]): Group<TRow>[] {
  const groups: Group<TRow>[] = [];
  const byParentId = new Map<string, Group<TRow>>();
  for (const row of rows) {
    if (row.parentId === null) {
      const group: Group<TRow> = { parent: row, children: [] };
      groups.push(group);
      byParentId.set(row.id, group);
      continue;
    }
    const parentGroup = byParentId.get(row.parentId);
    if (parentGroup) parentGroup.children.push(row);
    else groups.push({ parent: row, children: [] });
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function DragHandleIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 8 16" className="h-3.5 w-2 fill-current" aria-hidden="true">
      <circle cx="2" cy="3" r="1" />
      <circle cx="6" cy="3" r="1" />
      <circle cx="2" cy="8" r="1" />
      <circle cx="6" cy="8" r="1" />
      <circle cx="2" cy="13" r="1" />
      <circle cx="6" cy="13" r="1" />
    </svg>
  );
}

function CaretIcon({ expanded }: { expanded: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('h-3 w-3 transition-transform', expanded && 'rotate-90')}
      aria-hidden="true"
    >
      <path d="M9 5l7 7-7 7z" />
    </svg>
  );
}

function PencilIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function ListIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function ImagePlaceholderIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-4 w-4 text-pw-text-muted"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RowProps<TRow extends TaxonomyTableRow> {
  row: TRow;
  /** Indent only — the drag rules read `parentId`, never this. */
  level: 0 | 1;
  /**
   * The row's REAL parent id — `null` only for a true root. Never derived
   * from the indent: `groupRows` promotes a child whose parent sits on another
   * page to a top-level group, and calling that row a root would make it a
   * legal drop target for real roots, i.e. a cross-level payload.
   */
  parentId: string | null;
  hasChildren: boolean;
  expanded: boolean;
  zebra: boolean;
  showLabel: boolean;
  prefix: string;
  onToggleExpand: (id: string) => void;
  onToggleStatus: TaxonomyTableProps<TRow>['onToggleStatus'];
  onToggleDisplay: TaxonomyTableProps<TRow>['onToggleDisplay'];
  onEdit: TaxonomyTableProps<TRow>['onEdit'];
  onViewProducts: TaxonomyTableProps<TRow>['onViewProducts'];
  onDragStart: (e: DragEvent<HTMLTableRowElement>, row: TRow, parentId: string | null) => void;
  onDragOver: (
    e: DragEvent<HTMLTableRowElement>,
    targetId: string,
    parentId: string | null,
  ) => void;
  onDragEnd: () => void;
  onDrop: (e: DragEvent<HTMLTableRowElement>, targetId: string, parentId: string | null) => void;
  /** 'above' / 'below' draws the insertion rule; null when not a drop target. */
  dropEdge: 'above' | 'below' | null;
  dragging: boolean;
  busy: boolean;
}

function TaxonomyRow<TRow extends TaxonomyTableRow>({
  row,
  level,
  parentId,
  hasChildren,
  expanded,
  zebra,
  showLabel,
  prefix,
  onToggleExpand,
  onToggleStatus,
  onToggleDisplay,
  onEdit,
  onViewProducts,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  dropEdge,
  dragging,
  busy,
}: RowProps<TRow>): JSX.Element {
  const { t } = useTranslation('catalog');
  // `display` is optional on the wire (pre-migration rows); absent = on.
  const displayOn = row.display ?? true;

  // Every row may be picked up — a reorder is legal at both levels. Where it
  // may LAND is the restriction, and that is the drop check's job.
  const canDrag = !busy;

  return (
    <tr
      data-testid={`${prefix}-row-${row.id}`}
      data-drop-edge={dropEdge ?? undefined}
      draggable={canDrag}
      onDragStart={(e) => onDragStart(e, row, parentId)}
      onDragOver={(e) => onDragOver(e, row.id, parentId)}
      onDragEnd={onDragEnd}
      onDrop={(e) => onDrop(e, row.id, parentId)}
      className={cn(
        'h-14 border-b border-pw-border last:border-b-0',
        zebra ? 'bg-pw-surface/60' : 'bg-pw-bg',
        busy && 'opacity-60',
        // The row being carried fades; the row you would drop onto grows a
        // 2px brand rule on the edge the insert lands. Without either, an
        // HTML5 drag over a table gives no feedback at all and reads as
        // "drag-drop is not implemented".
        dragging && 'opacity-40',
        dropEdge === 'above' && 'shadow-[inset_0_2px_0_0_var(--pw-primary-bg)]',
        dropEdge === 'below' && 'shadow-[inset_0_-2px_0_0_var(--pw-primary-bg)]',
      )}
    >
      {/* grip — 40px */}
      <td className="w-10 pl-5 pr-0 align-middle">
        <span
          className={cn(
            'inline-flex text-pw-text-muted/50',
            canDrag ? 'cursor-grab' : 'cursor-not-allowed opacity-40',
          )}
          title={t('category.list.dragToReorder')}
          data-testid={`${prefix}-grip-${row.id}`}
        >
          <DragHandleIcon />
        </span>
      </td>

      {/* NAME — 280px, caret + indent express the 2 levels */}
      <td className="w-[280px] pr-3 align-middle">
        <div className="flex items-center gap-2" style={{ paddingLeft: level === 1 ? 32 : 0 }}>
          {level === 0 ? (
            <button
              type="button"
              aria-label={expanded ? t('category.list.collapse') : t('category.list.expand')}
              aria-expanded={hasChildren ? expanded : undefined}
              disabled={!hasChildren}
              onClick={() => onToggleExpand(row.id)}
              data-testid={`${prefix}-row-caret-${row.id}`}
              className={cn(
                'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-pw-text-muted',
                hasChildren ? 'hover:text-pw-text' : 'invisible',
              )}
            >
              <CaretIcon expanded={expanded} />
            </button>
          ) : (
            // Elbow connector, matching the design's child indent rail.
            <span
              aria-hidden="true"
              className="mb-2 inline-block h-3 w-3 shrink-0 rounded-bl-[2px] border-b border-l border-pw-border"
            />
          )}
          <span
            className={cn(
              'min-w-0 truncate text-sm text-pw-text',
              level === 0 ? 'font-semibold' : 'font-normal',
            )}
          >
            {row.name.en}
          </span>
        </div>
      </td>

      {/* LABEL — Collection only, 120px */}
      {showLabel ? (
        <td className="w-[120px] pr-3 align-middle">
          {row.label ? (
            <span
              className="text-sm text-pw-text"
              style={row.labelColor ? { color: row.labelColor } : undefined}
              data-testid={`${prefix}-label-${row.id}`}
            >
              {row.label}
            </span>
          ) : null}
        </td>
      ) : null}

      {/* IMAGE — 80px */}
      <td className="w-20 pr-3 align-middle">
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md border border-pw-border bg-pw-surface">
          {row.image ? (
            <img src={row.image} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <ImagePlaceholderIcon />
          )}
        </div>
      </td>

      {/* CREATED — 180px. The list is ordered by this column. */}
      <td className="w-[180px] whitespace-nowrap pr-3 align-middle text-[13px] text-pw-text-muted">
        {formatLastUpdate(row.createdAt)}
      </td>

      {/* LAST UPDATE — 180px */}
      <td className="w-[180px] whitespace-nowrap pr-3 align-middle text-[13px] text-pw-text-muted">
        {formatLastUpdate(row.updatedAt)}
      </td>

      {/* UPDATED BY — 200px */}
      <td className="w-[200px] truncate pr-3 align-middle text-[13px] text-pw-text-muted">
        {row.updatedByEmail !== null &&
        row.updatedByEmail !== undefined &&
        row.updatedByEmail !== ''
          ? row.updatedByEmail
          : '—'}
      </td>

      {/* STATUS — 100px */}
      <td className="w-[100px] pr-3 align-middle">
        <Switch
          checked={row.status === 'Active'}
          disabled={busy}
          onCheckedChange={(next) => onToggleStatus(row, next)}
          aria-label={t('category.list.statusToggleAria', { name: row.name.en })}
          data-testid={`${prefix}-status-toggle-${row.id}`}
        />
      </td>

      {/* DISPLAY — 100px */}
      <td className="w-[100px] pr-3 align-middle">
        <Switch
          checked={displayOn}
          disabled={busy}
          onCheckedChange={(next) => onToggleDisplay(row, next)}
          aria-label={t('category.list.displayToggleAria', { name: row.name.en })}
          data-testid={`${prefix}-display-toggle-${row.id}`}
        />
      </td>

      {/* ACTIONS — 100px */}
      <td className="w-[100px] pr-5 align-middle">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onEdit(row)}
            aria-label={t('category.list.editAria', { name: row.name.en })}
            data-testid={`${prefix}-edit-btn-${row.id}`}
            className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md border border-pw-border text-pw-text-muted transition-colors hover:bg-pw-bg-hover hover:text-pw-text"
          >
            <PencilIcon />
          </button>
          <button
            type="button"
            onClick={() => onViewProducts(row)}
            aria-label={t('category.list.viewProductsAria', { name: row.name.en })}
            data-testid={`${prefix}-products-btn-${row.id}`}
            className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md border border-pw-border text-pw-text-muted transition-colors hover:bg-pw-bg-hover hover:text-pw-text"
          >
            <ListIcon />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export function TaxonomyTable<TRow extends TaxonomyTableRow>({
  rows,
  showLabel = false,
  onToggleStatus,
  onToggleDisplay,
  onEdit,
  onViewProducts,
  onReorderSiblings,
  busy = false,
  testIdPrefix = 'categories',
}: TaxonomyTableProps<TRow>): JSX.Element {
  const { t } = useTranslation('catalog');
  const groups = useMemo(() => groupRows(rows), [rows]);

  // Collapsed set (not expanded set) so a parent arriving from the next page
  // defaults to expanded, matching the design.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  // The drag source lives in a ref, NOT in state. Calling setState from
  // `dragstart` re-renders the row that the browser is in the middle of
  // picking up, and Chrome answers that by cancelling the drag before the
  // first `dragover` ever fires — the grip moved and nothing else happened.
  // A ref is also readable in the same tick, so `dragover` never races the
  // commit. State below is only for the drop indicator, which is set from
  // `dragover` where a re-render is harmless.
  const dragRef = useRef<DragState | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; edge: 'above' | 'below' } | null>(
    null,
  );

  function toggleExpand(id: string): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearDrag(): void {
    dragRef.current = null;
    setDragId(null);
    setDropAt(null);
  }

  /**
   * The level, in the order the server sent it.
   *
   * Read off the flat row list by parentId, never off `groups`:
   * `groups.map(g => g.parent)` looks like the root level but is not, because
   * `groupRows` promotes a child whose parent sits on another page into its
   * own top-level group. That list mixed real roots with children and the
   * payload spanned two levels, which the server rejects outright
   * (CATEGORY_REORDER_CROSS_LEVEL, 409).
   */
  function siblingsOf(parentId: string | null): TRow[] {
    return rows.filter((r) => (r.parentId ?? null) === parentId);
  }

  function handleDragStart(
    e: DragEvent<HTMLTableRowElement>,
    row: TRow,
    parentId: string | null,
  ): void {
    if (busy) {
      e.preventDefault();
      return;
    }
    dragRef.current = { id: row.id, parentId };
    e.dataTransfer.effectAllowed = 'move';
    // Firefox refuses to start a drag unless dataTransfer carries something.
    e.dataTransfer.setData('text/plain', row.id);
    // Deferred so the browser has already taken its drag snapshot — see the
    // ref comment above; a synchronous re-render here kills the drag.
    requestAnimationFrame(() => setDragId(row.id));
  }

  function handleDragOver(
    e: DragEvent<HTMLTableRowElement>,
    targetId: string,
    targetParentId: string | null,
  ): void {
    const active = dragRef.current;
    if (!active) return;
    // Different level → no preventDefault, so the browser shows its own "no
    // drop" cursor. This is the whole rule: a drag reorders, it never
    // re-parents.
    if (active.parentId !== targetParentId) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (active.id === targetId) {
      setDropAt(null);
      return;
    }
    // The DIRECTION of travel picks the edge, not which half of the row the
    // pointer happens to be in (dbg-20260811-022808 iteration 3). The half
    // split made a drop onto the row directly above land the carried row back
    // in its own slot whenever the pointer was in that row's lower half — a
    // legitimate no-op, which the drop handler then discarded in silence. So
    // moving a row up one position worked or did nothing depending on a few
    // pixels of pointer position, which is exactly what "khi được khi không"
    // looks like. Dropping onto a row now always means "take that row's slot",
    // and the rule is drawn on the side the carried row will arrive from.
    const siblings = siblingsOf(targetParentId);
    const fromIdx = siblings.findIndex((s) => s.id === active.id);
    const toIdx = siblings.findIndex((s) => s.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const edge: 'above' | 'below' = fromIdx > toIdx ? 'above' : 'below';
    setDropAt((prev) =>
      prev?.id === targetId && prev.edge === edge ? prev : { id: targetId, edge },
    );
  }

  function handleDrop(
    e: DragEvent<HTMLTableRowElement>,
    targetId: string,
    targetParentId: string | null,
  ): void {
    e.preventDefault();
    const active = dragRef.current;
    clearDrag();
    if (!active) return;
    if (active.parentId !== targetParentId) return;
    if (active.id === targetId) return;

    const siblings = siblingsOf(targetParentId);
    const fromIdx = siblings.findIndex((s) => s.id === active.id);
    const toIdx = siblings.findIndex((s) => s.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    // The carried row TAKES the target's slot; the target and everything
    // between them shift by one to make room. Because source and target are
    // always different rows, this can never resolve to the order the level was
    // already in — there is no silent no-op left to explain away.
    const reordered = [...siblings];
    const [moved] = reordered.splice(fromIdx, 1);
    if (!moved) return;
    reordered.splice(toIdx, 0, moved);

    onReorderSiblings(reordered);
  }

  const th =
    'pr-3 text-left text-xs font-semibold uppercase tracking-wide text-pw-text-muted';

  // Zebra follows rendered position, not group index, so the stripe stays
  // uniform when a subtree is collapsed.
  let renderIndex = -1;
  const nextZebra = (): boolean => {
    renderIndex += 1;
    return renderIndex % 2 === 1;
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-pw-border bg-pw-bg">
      <table
        className="w-full min-w-[1180px] border-collapse"
        data-testid={`${testIdPrefix}-table`}
      >
        <thead>
          <tr className="h-9 border-b border-pw-border bg-pw-surface">
            <th className="w-10 pl-5 pr-0" aria-label={t('category.list.dragColumnAria')} />
            <th className={cn(th, 'w-[280px]')}>{t('category.list.colName')}</th>
            {showLabel ? (
              <th className={cn(th, 'w-[120px]')}>{t('collection.list.colLabel')}</th>
            ) : null}
            <th className={cn(th, 'w-20')}>{t('category.list.colImage')}</th>
            <th className={cn(th, 'w-[180px]')}>{t('category.list.colCreatedAt')}</th>
            <th className={cn(th, 'w-[180px]')}>{t('category.list.colLastUpdate')}</th>
            <th className={cn(th, 'w-[200px]')}>{t('category.list.colUpdatedBy')}</th>
            <th className={cn(th, 'w-[100px]')}>{t('category.list.colStatus')}</th>
            <th className={cn(th, 'w-[100px]')}>{t('category.list.colDisplay')}</th>
            <th className={cn(th, 'w-[100px] pr-5')}>{t('category.list.colActions')}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const isExpanded = !collapsed.has(group.parent.id);
            const hasChildren = group.children.length > 0;
            return (
              <Fragment key={group.parent.id}>
                <TaxonomyRow
                  row={group.parent}
                  level={0}
                  // Its real parent, not `null`. A promoted orphan renders at
                  // level 0 but still belongs to an off-page parent, and
                  // reporting it as a root made it a legal drop target for
                  // real roots — the drag-level guard then waved through a
                  // cross-level payload.
                  parentId={group.parent.parentId ?? null}
                  hasChildren={hasChildren}
                  expanded={isExpanded}
                  zebra={nextZebra()}
                  showLabel={showLabel}
                  prefix={testIdPrefix}
                  onToggleExpand={toggleExpand}
                  onToggleStatus={onToggleStatus}
                  onToggleDisplay={onToggleDisplay}
                  onEdit={onEdit}
                  onViewProducts={onViewProducts}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={clearDrag}
                  onDrop={handleDrop}
                  dropEdge={dropAt?.id === group.parent.id ? dropAt.edge : null}
                  dragging={dragId === group.parent.id}
                  busy={busy}
                />
                {isExpanded
                  ? group.children.map((child) => (
                      <TaxonomyRow
                        key={child.id}
                        row={child}
                        level={1}
                        parentId={group.parent.id}
                        hasChildren={false}
                        expanded={false}
                        zebra={nextZebra()}
                        showLabel={showLabel}
                        prefix={testIdPrefix}
                        onToggleExpand={toggleExpand}
                        onToggleStatus={onToggleStatus}
                        onToggleDisplay={onToggleDisplay}
                        onEdit={onEdit}
                        onViewProducts={onViewProducts}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={clearDrag}
                        onDrop={handleDrop}
                        dropEdge={dropAt?.id === child.id ? dropAt.edge : null}
                        dragging={dragId === child.id}
                        busy={busy}
                      />
                    ))
                  : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default TaxonomyTable;
